<template>
  <div class="card border border-danger border-2 bg-danger bg-opacity-10 shadow-sm">
    <!-- Header -->
    <div class="card-header bg-danger bg-opacity-25 border-bottom border-danger">
      <h5 class="fw-bold mb-0 text-danger">
        <i class="fas fa-skull-crossbones me-2"></i>Emergency Kill Switch
      </h5>
    </div>

    <div class="card-body">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-danger" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <template v-else>
        <!-- Status badge -->
        <div class="mb-3">
          <span v-if="isActive" class="badge bg-danger fs-6 px-3 py-2">
            <i class="fas fa-stop-circle me-2"></i>ACTIVE — SYSTEM HALTED
          </span>
          <span v-else class="badge bg-success fs-6 px-3 py-2">
            <i class="fas fa-check-circle me-2"></i>INACTIVE — System Running
          </span>

          <div v-if="isActive && activatedAt" class="mt-2 text-muted small">
            <i class="fas fa-clock me-1"></i>Activated: {{ formatTimestamp(activatedAt) }}
          </div>
        </div>

        <!-- Page-level error -->
        <div v-if="error" class="alert alert-danger py-2">
          <i class="fas fa-exclamation-circle me-2"></i>{{ error }}
        </div>

        <!-- Deactivate button (when active) -->
        <div v-if="isActive && !showConfirm">
          <button class="btn btn-success" :disabled="actionLoading" @click="deactivate">
            <span v-if="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fas fa-play-circle me-2"></i>Deactivate Kill Switch
          </button>
        </div>

        <!-- Activate button (when inactive, no confirm open) -->
        <div v-if="!isActive && !showConfirm">
          <button class="btn btn-outline-danger" @click="showConfirm = true">
            <i class="fas fa-power-off me-2"></i>Activate Kill Switch
          </button>
        </div>

        <!-- Inline confirmation section -->
        <div v-if="showConfirm" class="mt-3 p-3 border border-danger rounded bg-white">
          <p class="fw-bold text-danger mb-2">
            <i class="fas fa-exclamation-triangle me-2"></i>Warning — this action will:
          </p>
          <ul class="text-dark mb-3">
            <li>Immediately halt <strong>all trading activity</strong> across every service</li>
            <li>Place <strong>market orders to flatten all open positions</strong> (SELL every LONG, BUY to cover every SHORT)</li>
            <li>Block any new BUY orders from being placed</li>
          </ul>

          <div class="mb-3">
            <label class="form-label fw-semibold text-danger">Type CONFIRM to proceed</label>
            <input
              v-model="confirmText"
              type="text"
              class="form-control border-danger"
              placeholder="CONFIRM"
              autocomplete="off"
            />
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-danger"
              :disabled="confirmText !== 'CONFIRM' || actionLoading"
              @click="activate"
            >
              <span v-if="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fas fa-skull-crossbones me-2"></i>Confirm — Halt System &amp; Sell All
            </button>
            <button class="btn btn-secondary" @click="cancelConfirm">
              Cancel
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getKillSwitchStatus, activateKillSwitch, deactivateKillSwitch } from '../services/killSwitchApi.js';

export default {
  name: 'KillSwitchPanel',

  emits: ['status-changed'],

  data() {
    return {
      loading: true,
      actionLoading: false,
      error: null,
      isActive: false,
      activatedAt: null,
      showConfirm: false,
      confirmText: '',
    };
  },

  async mounted() {
    await this.loadStatus();
  },

  methods: {
    async loadStatus() {
      this.loading = true;
      this.error = null;
      try {
        console.log('[KillSwitchPanel] Fetching kill switch status.');
        const data = await getKillSwitchStatus();
        this.isActive = data.active;
        this.activatedAt = data.activated_at;
        console.log(`[KillSwitchPanel] Status loaded — active: ${data.active}`);
      } catch (err) {
        this.error = err.message;
        console.error('[KillSwitchPanel] Failed to load status:', err);
      } finally {
        this.loading = false;
      }
    },

    async activate() {
      this.actionLoading = true;
      this.error = null;
      try {
        console.log('[KillSwitchPanel] Activating kill switch.');
        const data = await activateKillSwitch(this.confirmText);
        this.isActive = data.active;
        this.activatedAt = data.activated_at;
        this.showConfirm = false;
        this.confirmText = '';
        console.log('[KillSwitchPanel] Kill switch activated.');
        this.$emit('status-changed', true);
      } catch (err) {
        this.error = err.message;
        console.error('[KillSwitchPanel] Failed to activate kill switch:', err);
      } finally {
        this.actionLoading = false;
      }
    },

    async deactivate() {
      this.actionLoading = true;
      this.error = null;
      try {
        console.log('[KillSwitchPanel] Deactivating kill switch.');
        const data = await deactivateKillSwitch();
        this.isActive = data.active;
        this.activatedAt = data.activated_at;
        console.log('[KillSwitchPanel] Kill switch deactivated.');
        this.$emit('status-changed', false);
      } catch (err) {
        this.error = err.message;
        console.error('[KillSwitchPanel] Failed to deactivate kill switch:', err);
      } finally {
        this.actionLoading = false;
      }
    },

    cancelConfirm() {
      this.showConfirm = false;
      this.confirmText = '';
      this.error = null;
    },

    formatTimestamp(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },
  },
};
</script>
